import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { formatPrice } from '../utils/currency';

const MenuManagement = ({ apiClient }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(''); // Category filter
  const [showForm, setShowForm] = useState(false);
  const [currency, setCurrency] = useState('USD'); // Restaurant currency
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    price: '',
    is_available: true
  });
  const [descriptionWarning, setDescriptionWarning] = useState('');

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
    fetchRestaurantCurrency();
  }, []);

  const fetchRestaurantCurrency = async () => {
    try {
      const response = await apiClient.get('/restaurant/profile');
      setCurrency(response.data.currency || 'USD');
    } catch (error) {
      console.error('Error fetching restaurant currency:', error);
      // Default to USD if fetch fails
      setCurrency('USD');
    }
  };

  // Filter menu items when selectedCategory or menuItems change
  useEffect(() => {
    if (selectedCategory === '') {
      setFilteredMenuItems(menuItems);
    } else {
      setFilteredMenuItems(menuItems.filter(item => item.category_id === selectedCategory));
    }
  }, [selectedCategory, menuItems]);

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/restaurant/menu-items');
      setMenuItems(response.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/restaurant/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;
    
    // Handle description character limit
    if (name === 'description') {
      if (finalValue.length > 150) {
        // Truncate and show warning
        finalValue = finalValue.substring(0, 150);
        setDescriptionWarning('Description has been truncated to 150 characters.');
      } else {
        setDescriptionWarning('');
      }
    }
    
    setFormData({
      ...formData,
      [name]: finalValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const itemData = {
        ...formData,
        price: parseFloat(formData.price)
      };

      let itemId;
      if (editingItem) {
        await apiClient.put(`/restaurant/menu-items/${editingItem.id}`, itemData);
        itemId = editingItem.id;
        toast.success('Menu item updated successfully!');
      } else {
        const response = await apiClient.post('/restaurant/menu-items', itemData);
        itemId = response.data.id;
        toast.success('Menu item created successfully!');
      }

      resetForm();
      fetchMenuItems();
    } catch (error) {
      console.error('Error saving menu item:', error);
      toast.error('Failed to save menu item');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      category_id: item.category_id,
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      is_available: item.is_available
    });
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await apiClient.delete(`/restaurant/menu-items/${item.id}`);
      toast.success('Menu item deleted successfully!');
      fetchMenuItems();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      toast.error('Failed to delete menu item');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      name: '',
      description: '',
      price: '',
      is_available: true
    });
    setDescriptionWarning('');
    setEditingItem(null);
    setShowForm(false);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block">
              <div className="spinner w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 mt-3">Loading menu items...</p>
          </div>
        </div>
      )}

      {!loading && (
        <>
          <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Menu Items</h2>
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-menu-item-button"
              className="btn-primary"
              disabled={categories.length === 0}
            >
              Add Menu Item
            </button>
          </div>
          {categories.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Create categories first before adding menu items.
            </p>
          )}
        </div>

        {/* Category Filter */}
        {categories.length > 0 && menuItems.length > 0 && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              <label htmlFor="category-filter" className="text-sm font-medium text-gray-700">
                Filter by Category:
              </label>
              <select
                id="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="form-input w-64"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {selectedCategory && (
                <span className="text-sm text-gray-600">
                  Showing {filteredMenuItems.length} of {menuItems.length} items
                </span>
              )}
            </div>
          </div>
        )}

        {/* Menu Item Form */}
        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-4">
              {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="category_id" className="form-label">
                    Category *
                  </label>
                  <select
                    id="category_id"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    data-testid="menu-item-category-select"
                    className="form-input"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="name" className="form-label">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    data-testid="menu-item-name-input"
                    className="form-input"
                    maxLength={35}
                    required
                  />
                  <div className="mt-1">
                    <span className={`text-sm ${formData.name.length > 30 ? 'text-orange-600' : formData.name.length === 35 ? 'text-red-600' : 'text-gray-500'}`}>
                      {formData.name.length}/35 characters
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="description" className="form-label">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  data-testid="menu-item-description-input"
                  className="form-textarea"
                  rows={3}
                  required
                />
                <div className="mt-1 flex justify-between items-center">
                  <span className={`text-sm ${formData.description.length > 150 ? 'text-red-600' : 'text-gray-500'}`}>
                    {formData.description.length}/150 characters
                  </span>
                  {descriptionWarning && (
                    <span className="text-sm text-orange-600">⚠️ {descriptionWarning}</span>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="price" className="form-label">
                  Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  data-testid="menu-item-price-input"
                  className="form-input"
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_available"
                  name="is_available"
                  checked={formData.is_available}
                  onChange={handleInputChange}
                  data-testid="menu-item-available-checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_available" className="ml-2 block text-sm text-gray-900">
                  Available for ordering
                </label>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="save-menu-item-button"
                  className="btn-primary"
                >
                  {loading ? <div className="spinner"></div> : (editingItem ? 'Update' : 'Create')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-menu-item-button"
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Menu Items Grid */}
        <div className="menu-grid">
          {filteredMenuItems.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500" data-testid="no-menu-items-message">
              {menuItems.length === 0 ? 
                "No menu items created yet. Click \"Add Menu Item\" to get started." :
                `No menu items found${selectedCategory ? ' in selected category' : ''}.`
              }
            </div>
          ) : (
            filteredMenuItems.map((item) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`menu-item-${item.id}`}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900" data-testid={`menu-item-name-${item.id}`}>
                      {item.name}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        item.is_available
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {item.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2" data-testid={`menu-item-category-${item.id}`}>
                    {getCategoryName(item.category_id)}
                  </p>
                  <p className="text-sm text-gray-600 mb-3" data-testid={`menu-item-description-${item.id}`}>
                    {item.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900" data-testid={`menu-item-price-${item.id}`}>
                      {formatPrice(item.price, currency)}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        data-testid={`edit-menu-item-${item.id}`}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        data-testid={`delete-menu-item-${item.id}`}
                        className="text-sm text-red-600 hover:text-red-800"
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default MenuManagement;